
import { IonContent, IonItem, IonLabel, IonModal, IonRadio, IonRadioGroup } from "@ionic/react";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

// Question interface
interface QuestionData {
    question: string;
    correct: boolean;
}


const TrueFalse: React.FC = () => {
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const [selectedOption, setSelectedOption] = useState<string | boolean | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showImage, setShowImage] = useState(false);
    const [error, setError] = useState(false);


    const modal = useRef<HTMLIonModalElement>(null);

    // Fetch question data from an external source (replace with your actual API or JSON URL)
    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'TrueFalseArticle.json');
                data = await response.json();
            } else {
                const module = await import('../../../../datas/TrueFalseArticle.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        // Simulating an API fetch to load questions
        fetchQuestions();
        document.title = "True or False – Use of A or An | LKG Grammar Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleOptionClick = (selectedMyOption: boolean) => {
        console.log(selectedMyOption)
        const currentQuestion = questions[currentIndex];
        setSelectedOption(selectedMyOption);
        if (selectedMyOption === currentQuestion.correct) {
            setError(false);
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                }
                setShowImage(false);
                setError(false);
                setSelectedOption(null)
            }, 5000); // Shortened delay to 3 seconds
        } else {
            setError(true);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Teach LKG kids to identify correct usage of articles with the True or False activity on Mama’s Guidance—interactive exercises to practice “a,” “an,” and “the” in fun sentence formats.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart seasons additionWord" style={{ marginTop: "0%" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>True or False – Use of A or An</h1>

                                    <p>
                                        This engaging <strong>True or False worksheet</strong> helps LKG students understand the correct use of articles – <strong>“a“</strong> and <strong>“an“</strong>. With simple picture-based or sentence-based statements, children identify whether the article usage is <strong>true or false</strong>, developing a strong foundation in English grammar.
                                    </p>

                                    <h2>🔠 What’s in the Worksheet?</h2>
                                    <p>
                                        The worksheet features a series of <strong>statements using “a” or “an”</strong>, such as <strong>“a apple”</strong> or <strong>“an cat.”</strong> Children will read or listen to the statements and <strong>mark them as True ✅ or False ❌</strong> based on the correct grammar rule.
                                    </p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Understand the rules for using <strong>“a” and “an”</strong> before words</li>
                                        <li>Identify correct and incorrect article usage</li>
                                        <li>Improve <strong>grammar, listening, and comprehension skills</strong></li>
                                        <li>Strengthen phonetic awareness (<strong>vowel vs. consonant sounds</strong>)</li>
                                    </ul>

                                    <h2>👩‍🏫 How to Use the Worksheet</h2>
                                    <ul>
                                        <li>Read each statement aloud with your child.</li>
                                        <li>Ask them to listen to the word following the article.</li>
                                        <li><strong>If the word starts with a vowel sound, “an” is correct. For consonant sounds, “a” is correct</strong>.</li>
                                        <li>Mark each sentence True or False accordingly.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/english-language/one-and-many"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>One and Many</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/english-language/rhyming-words"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Rhyming Words</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/english-language/opposites"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Opposites</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why This Activity Works</h2>
                                    <p>
                                        True/False exercises promote <strong>critical thinking and instant recall</strong>. Instead of choosing or writing, children must decide quickly if a sentence is correct – a powerful way to reinforce grammar concepts like article usage.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>True or False – Use of A or An</h1>

                                <p>
                                    This engaging <strong>True or False worksheet</strong> helps LKG students understand the correct use of articles – <strong>“a“</strong> and <strong>“an“</strong>. With simple picture-based or sentence-based statements, children identify whether the article usage is <strong>true or false</strong>, developing a strong foundation in English grammar.
                                </p>

                                <h2>🔠 What’s in the Worksheet?</h2>
                                <p>
                                    The worksheet features a series of <strong>statements using “a” or “an”</strong>, such as <strong>“a apple”</strong> or <strong>“an cat.”</strong> Children will read or listen to the statements and <strong>mark them as True ✅ or False ❌</strong> based on the correct grammar rule.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Understand the rules for using <strong>“a” and “an”</strong> before words</li>
                                    <li>Identify correct and incorrect article usage</li>
                                    <li>Improve <strong>grammar, listening, and comprehension skills</strong></li>
                                    <li>Strengthen phonetic awareness (<strong>vowel vs. consonant sounds</strong>)</li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Read each statement aloud with your child.</li>
                                    <li>Ask them to listen to the word following the article.</li>
                                    <li><strong>If the word starts with a vowel sound, “an” is correct. For consonant sounds, “a” is correct</strong>.</li>
                                    <li>Mark each sentence True or False accordingly.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/english-language/one-and-many"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>One and Many</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/english-language/rhyming-words"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Rhyming Words</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/english-language/opposites"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Opposites</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Activity Works</h2>
                                <p>
                                    True/False exercises promote <strong>critical thinking and instant recall</strong>. Instead of choosing or writing, children must decide quickly if a sentence is correct – a powerful way to reinforce grammar concepts like article usage.
                                </p>
                            </article>
                            <div className="mt-4 mb-4">
                                <h3 className="mt-2 mb-3">
                                    {questions[currentIndex]?.question}
                                </h3>
                            </div>

                            <IonRadioGroup onIonChange={(e) => handleOptionClick(e.detail.value!)}>
                                <IonItem
                                    className={`grid-item ${selectedOption === true
                                        ? true === questions[currentIndex]?.correct
                                            ? "correct"
                                            : "errorItems"
                                        : ""
                                        }`}>
                                    <IonRadio slot="start" value={true} />
                                    <IonLabel>True</IonLabel>
                                </IonItem>
                                <IonItem
                                    className={`grid-item ${selectedOption === false
                                        ? false === questions[currentIndex]?.correct
                                            ? "correct"
                                            : "errorItems"
                                        : ""
                                        }`}>
                                    <IonRadio slot="start" value={false} />
                                    <IonLabel>False</IonLabel>
                                </IonItem>
                            </IonRadioGroup>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default TrueFalse;
